/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 3 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation } from "@i18n";
import { getFilesList } from "@Services/dataManager";
import { convertDate } from "@Services/luxonHelper";
import { getReviewUrl } from "@Services/utils/helpers";
import { getViewerUrl, MAX_FILE_UPLOAD_SIZE, formCategories } from "@Helpers";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import FileVersion from ".";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { QueryBuilder, executeQuery } from "@Services/queryBuilder";
import { getStatusBtnStyle } from "@Features/reviews";
import { getFilesGridColumns } from "./FilesGridHelper";
import type { FilesGridColumn, ReviewLevelOrSentinel } from "./FilesGridHelper";
import type {
  GroupCondition,
  LayoutData,
  SlickGrid1Ref,
  ToolbarItem,
  ValidatedColumnDefinition,
} from "@CubeComponents/html/SlickGrid1";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import axiosWrapper from "@Services/axiosWrapper";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import { showToastNotification } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR } from "@Services/constants";
import type { GetFormTemplateResponse, GetAllUsedFormTemplatesResponse } from "@ServerTypes/projects/project.schema";
import _ from "lodash";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import ToggleSwitchComponent from "@CubeComponents/form/ToggleSwitchComponent";
import DropdownButtonComponent from "@CubeComponents/form/DropdownButtonComponentV1";
// Identity assertion: the alias is emitted, so it stays; only the `any` is dropped.
const TypedDropdownButtonComponent = DropdownButtonComponent as typeof DropdownButtonComponent;

import { renderUserTag } from "@CubeComponents/html/userTagDom";
import {
  setCurrentFolderInFileManager,
  setIsFileVersionUploading,
  setCurrentFileUploadProperties,
  minimiseFileUpload,
  maximiseFileUpload,
  closeFileUpload,
  emptyFileUploadData,
} from "@Actions/fileUpload";

import { useSearchParams, useLocation } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import SplitLayout from "@CubeComponents/html/SplitLayout/SplitLayout";
import SlickGrid1 from "@CubeComponents/html/SlickGrid1";
import { GridPageLayout, GridPageHeaderLeft } from "@CubeComponents/html/GridPageLayout";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { FileUploadFormTemplate } from "@Reducers/minimiseFileUpload";

interface ShowToolbarOptions {
  views?: boolean;
  group?: boolean;
  upload?: boolean;
  action?: boolean;
  pinnedColumns?: boolean;
}

interface FilesGridProps {
  initialShowAllVersions?: boolean;
  showProjectID?: boolean;
  projectId?: string | null;
  showTitle?: boolean;
  showToolbar?: ShowToolbarOptions;
  propertiesPanel?: boolean;
  excelExport?: boolean;
  handleFileSelectionCallback?: (selectedFiles: SelectedFilePayload[]) => void;
  spaceId?: string | null;
  showOnlyMergedFile?: boolean;
  disableMenuOptions?: boolean;
  showUpload?: boolean;
  disableFiles?: string[];
}

/**
 * A row of this grid. Declared as a `type` alias, NOT an `interface`, on purpose: only a type alias
 * gets an implicit index signature, and `FilesGridRow` (the helper's row constraint) declares one —
 * an `interface` here fails `TRow extends FilesGridRow` with no obvious cause.
 */
type FileData = {
  /** Custom-field columns address values by dotted path (`customFields.<questionId>`), so `field`
   *  — which is `keyof FileData` — has to admit an arbitrary string. */
  [key: string]: unknown;
  id: string | number;
  fileVersionId?: string;
  fileName: string;
  fileType?: string;
  size?: number;
  createdDate: string;
  createdBy: {
    _id: ObjectIdString;
  };
  file?: {
    space?: {
      id: string;
      project?: {
        id: string;
        projectId: string;
      };
    };
    indexAtProjectLevel: number;
  };
  fileVersionNumber: number;
  isContainer?: boolean;
  resourceType?: string;
  reviews?: FileReviewSummary;
  customFields?: Record<string, unknown>;
  parentFileVersionId?: string | null;
  currentReviewLevel?: ReviewLevelOrSentinel;
  reviewName?: string;
  serialId?: string | number;
  reviewFlow?: string;
  reviewStatusName?: string;
  nextActionDueDate?: string | number;
  isRowDisabled?: boolean;
};

/**
 * `GetFormTemplateResponse` types `template` as `unknown[]`, so the element shape is declared here
 * from what `processTemplate` reads off it.
 */
type FormTemplateSection = { questionFields?: FormTemplateQuestion[]; sectionHeading?: string };

/**
 * The saved view this grid applies. `LayoutData` is SlickGrid1's shape; the two extra keys are what
 * `onClickLayout` actually destructures.
 *
 * KNOWN BUG, preserved verbatim: `LayoutData` declares the grouping list as `groupConditions`, but
 * `onClickLayout` reads `group` — so unless a view document carries a legacy `group` key, the
 * grouping branch is dead and the `else` branch always runs. Verify against a saved view before
 * changing it; renaming the read would alter behaviour.
 */
type FilesGridSavedView = Omit<LayoutData, "pinnedColumns"> & {
  group?: GroupCondition[];
  enableAllVersions?: boolean;
  /**
   * DIVERGENCE from `LayoutData`, which declares `pinnedColumns?: string[]`. This grid passes the
   * value straight to `setOptions({ frozenColumn })`, which takes a column INDEX. One of the two
   * declarations is wrong; typed here as what this file actually does with it.
   */
  pinnedColumns?: number;
};

/** One question inside a form-template section; each becomes a custom-field grid column. */
type FormTemplateQuestion = { _id: string; fieldType?: string; questionValue?: string };

/**
 * A file record exactly as `getFilesList` returns it. Deliberately loose: the query is built through
 * `QueryBuilder`/`executeQuery`, which have no response schema, and this component both reads
 * arbitrary keys off it and spreads it wholesale into a row. Typing it `unknown`-valued keeps every
 * read explicit without pretending to a shape the server does not guarantee.
 */
type RawFileRecord = { [key: string]: unknown; id?: unknown; fileName?: string; resourceType?: string };

/** A review record as the same untyped query returns it, before `mapReviewsByVersionId` flattens it. */
type RawReviewRecord = {
  _id?: ObjectIdString;
  createdDate?: string | number;
  name?: string;
  reviewFlow?: { name?: string };
  serialId?: string | number;
  currentReviewLevel?: ReviewLevelOrSentinel;
  nextActionDueDate?: string | number;
  reviewStatus?: string;
  initiationDate?: number;
  finishedDate?: number;
  fileVersionStatuses?: { fileVersionId?: string; status?: { name?: string; category?: string } }[];
};

/**
 * Aliased to the reducer's type rather than re-declared: `formTemplateRef` is handed straight to
 * `FileUpload`, whose prop is `FileUploadFormTemplate`, so a local copy could only drift from it.
 */
type FormTemplate = FileUploadFormTemplate;

/**
 * One review as `mapReviewsByVersionId` flattens it onto a row. Not a server response type: it is a
 * projection this component builds from the reviews list, keyed by file-version id.
 */
type FileReviewSummary = {
  _id?: ObjectIdString;
  createdDate?: string | number;
  name?: string;
  reviewFlow?: { name?: string };
  status?: { name?: string; category?: string };
  serialId?: string | number;
  currentReviewLevel?: ReviewLevelOrSentinel;
  nextActionDueDate?: string | number;
  reviewStatus?: string;
  initiationDate?: number;
  finishedDate?: number;
};

/** The row shape `handleFileSelectionCallback` receives — a flattened picker payload, not a file. */
type SelectedFilePayload = {
  id: unknown;
  hierarchyId: unknown;
  name: unknown;
  userId: unknown;
  dateCreated: unknown;
  versionNumber: unknown;
  fileVersion: unknown;
  size: unknown;
  conversionStatus: unknown;
  isContainer: unknown;
  parentId: unknown;
  resourceType: unknown;
  project: unknown;
  space: unknown;
  spaceId: unknown;
  fileType: unknown;
  engineType: unknown;
  isFile: unknown;
};

/**
 * The custom-field columns this grid derives from form templates. Aliased to the helper's column
 * type rather than re-declared: the local copy duplicated `ColumnDefinition` from SlickGrid1 and
 * widened it with `[key: string]: any` plus an untyped `_valueGetter`, so `extraColumns` did not
 * actually agree with what `getFilesGridColumns` merges them into.
 */
type ColumnDefinition = FilesGridColumn<FileData>;

interface FilesGridHeaderProps {
  projectId?: string | null;
  showAllVersions: boolean;
  onToggleVersions: () => void;
}

const FilesGridHeader = memo(function FilesGridHeader({
  projectId,
  showAllVersions,
  onToggleVersions,
}: FilesGridHeaderProps) {
  return (
    <div className="flex items-center gap-4 justify-between pb-4">
      <GridPageHeaderLeft
        icon="FilesIcon"
        title="Files"
        description={
          projectId
            ? "Manage all the files available in the current project"
            : "Manage all the files available in the current portal"
        }
      />
      <div className="flex items-center gap-2">
        <ToggleSwitchComponent checked={showAllVersions} onChange={onToggleVersions} />
        <span>{`Showing ${showAllVersions ? "all" : "only latest"} versions`}</span>
      </div>
    </div>
  );
});

function FilesGrid({
  initialShowAllVersions = true,
  showProjectID = false,
  projectId,
  spaceId,
  showTitle = true,
  showToolbar = { views: true, group: true, upload: true, action: true, pinnedColumns: true },
  propertiesPanel = true,
  excelExport = true,
  handleFileSelectionCallback,
  showOnlyMergedFile,
  disableMenuOptions,
  disableFiles,
}: Readonly<FilesGridProps>) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const byPortal = searchParams.get("byPortal");
  const location = useLocation();

  const fileUploadList = useSelector((state: RootState) => state.fileUploadList);
  const fileUploadState = useSelector((state: RootState) => state.fileUploadState);
  const importOnlyUploads = useSelector((state: RootState) => !!state.layout.FILE_MANAGER?.IMPORT_ONLY_UPLOADS);
  const popupRef = useRef<HTMLDivElement | null>(null);
  const [showProperties, setShowProperties] = useState<boolean>(false);
  const [gridReady, setGridReady] = useState<boolean>(false);
  const [originalFilesData, setOriginalFilesData] = useState<FileData[]>([]);
  const [filesData, setFilesData] = useState<FileData[]>([]);
  const [groupActive, setGroupActive] = useState<boolean>(false);
  const [filterActive, setFilterActive] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string[] | number[]>([]);
  const [customFields, setCustomFields] = useState<Record<string, ColumnDefinition>>({});
  const [showLoader, setShowLoader] = useState<boolean>(true);

  const [layoutDropdown, setLayoutDropdown] = useState<boolean>(false);
  const [showAllVersions, setShowAllVersions] = useState<boolean>(initialShowAllVersions);
  const showAllVersionsRef = useRef<boolean>(initialShowAllVersions);
  const [triggerUpdateCurrentView, setTriggerUpdateCurrentView] = useState<FilesGridSavedView | false>(false);
  const [preventUpdate, setPreventUpdate] = useState<boolean>(false);
  const preventUpdateCurrentView = useRef<boolean>(false);
  const [fileVersionId, setFileVersionId] = useState<string[]>([]);
  const formTemplateRef = useRef<FormTemplate>({ template: [], formTemplateId: "" });
  const fileUploadStateRef = useRef<string | null>(null);

  const handleClickOutside = (event: MouseEvent) => {
    if (popupRef.current && !popupRef.current.contains(event.target as Node)) {
      layoutDropdown && setLayoutDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleClickOutside);
    fetchAllUsedFormTemplates();
    if (!byPortal && projectId) {
      getFormTemplate();
    }
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    fileUploadStateRef.current = fileUploadState;
    if (fileUploadState === "CLOSED") {
      if (reactGridRef.current?.dataView) {
        loadData(0, 1000000000);
      }
      dispatch(setIsFileVersionUploading(false));
      dispatch(closeFileUpload());
      dispatch(emptyFileUploadData());
    }
  }, [fileUploadState]);

  useEffect(() => {
    if (fileUploadState === "CLOSED") return;
    if (fileUploadList.length) {
      dispatch(minimiseFileUpload());
    } else {
      dispatch(closeFileUpload());
    }
  }, [location]);

  const handleUpdateCurrentView = (data: FilesGridSavedView) => {
    if (!preventUpdateCurrentView.current) {
      setTriggerUpdateCurrentView(data);
    }
  };

  useEffect(() => {
    preventUpdateCurrentView.current = preventUpdate;
  }, [preventUpdate]);

  useEffect(() => {
    showAllVersionsRef.current = showAllVersions;
  }, [showAllVersions]);

  // Helper function to filter files to only latest versions
  const filterToLatestVersions = (data: FileData[]): FileData[] => {
    const latestDataMap = new Map<string, FileData>();
    for (const item of data) {
      const projectId = item.file?.space?.project?.projectId;
      const indexAtProjectLevel = item.file?.indexAtProjectLevel;
      if (projectId === undefined || indexAtProjectLevel === undefined) {
        continue;
      }
      const key = `${projectId}-${indexAtProjectLevel}`;
      const existingItem = latestDataMap.get(key);
      if (!existingItem || item.fileVersionNumber > existingItem.fileVersionNumber) {
        latestDataMap.set(key, item);
      }
    }
    return Array.from(latestDataMap.values());
  };

  async function onClickLayout(clickLayout: FilesGridSavedView): Promise<void> {
    /*
      Both syncronours and asyncronous update is required
    */
    setPreventUpdate(true);
    preventUpdateCurrentView.current = true;
    /*
      end
    */

    const params = clickLayout;

    if (Object.keys(params).length !== 0) {
      // Except "All Files" View
      const { group, pinnedColumns, enableFiltering, enableGrouping, enableAllVersions } = params;

      // The initial saved view resolves before the grid mounts; apply the state parts now
      // (they belong in the same batch as the initial fetch) and replay the grid-instance
      // operations once the grid is ready.
      if (!reactGridRef.current?.slickGrid) {
        pendingLayoutParamsRef.current = params;
        setGroupActive(!!enableGrouping);
        setFilterActive(!!enableFiltering);
        setShowAllVersions(!!enableAllVersions);
        setPreventUpdate(false);
        return;
      }

      if (Array.isArray(group) && group.length) {
        // Erased assertions: the `!reactGridRef.current?.slickGrid` guard above already returned.
        // KNOWN BUG, preserved verbatim: `setGroupConditions` expects `{ column: string }`, but the
        // whole `groupItem` object is passed as `column`. Also `frozenColumn` expects a column INDEX
        // while `pinnedColumns` is a string[] of column ids.
        // The assertion is on the FUNCTION, not the value: `setGroupConditions` declares
        // `{ column: string }` but this call site passes the whole group object as `column`.
        // Asserting the signature keeps the payload verbatim and documents the mismatch.
        // KNOWN BUG, preserved verbatim: `setGroupConditions` declares `{ column: string }`, but the
        // whole group object is passed as `column`. The assertion is an intersection that includes
        // the real argument type, so it is a single erased `as` and the payload is unchanged.
        reactGridRef.current!.gridState!.setGroupConditions!(
          group.map((groupItem: GroupCondition) => ({
            column: groupItem,
            direction: groupItem.direction || "asc",
          })) as ({ column: GroupCondition; direction?: string } & GroupCondition)[],
        );
        (
          reactGridRef.current!.gridService as SlickGrid1Ref<FileData>["gridService"] & {
            _grid: { invalidate: () => void };
          }
        )._grid.invalidate();
      } else {
        reactGridRef.current!.slickGrid.setOptions({
          frozenColumn: pinnedColumns,
        });
      }
      setGroupActive(!!enableGrouping);
      setFilterActive(!!enableFiltering);
      setShowAllVersions(!!enableAllVersions);
    }
    setPreventUpdate(false);
  }

  const getFormTemplate = async (): Promise<void> => {
    const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, {
      projectId,
      resourceType: formCategories.files,
    });

    if (response.data.status && response.data.data) {
      const formData = response.data.data;
      const template = (formData?.template || []) as FormTemplate["template"];
      formTemplateRef.current = {
        template,
        formTemplateId: formData._id,
      };
    }
  };

  const [filesGridColumns, setFilesGridColumns] = useState<FilesGridColumn<FileData>[]>([]);

  const createColumnFromQuestion = (
    question: { _id: string; fieldType?: string; questionValue?: string },
    sectionHeading: string,
  ): ColumnDefinition => {
    const typeMap: Record<string, string> = {
      "single-line": "Text",
      "multi-line": "Text",
      "date-time": "Date",
      "multi-select": "MultiSelect",
    };

    // Erased: `fieldType` is optional, and indexing with `undefined` is exactly what happens today —
    // the lookup misses and the `|| ""` fallback takes over. Behaviour unchanged.
    const filterType = typeMap[question.fieldType!] || "";

    const columnDef: ColumnDefinition = {
      field: `customFields.${question._id}`,
      id: `${question._id}`,
      width: 150,
      originalWidth: 150,
      textAlign: "Left",
      headerText: question.questionValue,
      // Erased: `typeMap` misses on an unmapped fieldType and falls back to `""`, which is NOT a
      // member of `FilterType`. Preserved verbatim — the grid treats it as "no filter" today.
      filterType: filterType as ColumnDefinition["filterType"],
      _valueGetter: (args: FileData) => {
        const value = _.get(args, `customFields.${question._id}`);
        if (question.fieldType === "date-time") {
          if (!value) return null;
          return Number(value);
        }
        return value;
      },
      _excelValueGetter: (args: { customFields?: Record<string, unknown> }) => {
        const value = args.customFields?.[question._id];
        if (value === null || value === undefined || value === "") return "";
        if (question.fieldType === "date-time") return convertDate(Number(value), "dateForTooltip");
        return value;
      },
      name: question.questionValue,
      columnGroup: sectionHeading,
    };

    // Add formatter for date-time fields to display formatted date
    if (question.fieldType === "date-time") {
      columnDef.formatter = (_row: number, _cell: number, _value: unknown, _columnDef, dataContext: FileData) => {
        const value = _.get(dataContext, `customFields.${question._id}`);
        if (!value) return "";
        return convertDate(Number(value), "dateForText");
      };
    }

    return columnDef;
  };

  const processQuestionFields = (
    questionFields: FormTemplateQuestion[],
    sectionHeading: string,
    extraColumns: Record<string, ColumnDefinition>,
  ): void => {
    if (!questionFields) return;
    for (const question of questionFields) {
      extraColumns[question._id] = createColumnFromQuestion(question, sectionHeading);
    }
  };

  const processTemplate = (template: FormTemplateSection[], extraColumns: Record<string, ColumnDefinition>): void => {
    if (!template) return;
    for (const section of template) {
      const { questionFields, sectionHeading } = section;
      processQuestionFields(questionFields!, sectionHeading!, extraColumns);
    }
  };

  const processTemplatesData = (data: { template?: FormTemplateSection[] }[]): Record<string, ColumnDefinition> => {
    const extraColumns: Record<string, ColumnDefinition> = {};
    for (const item of data) {
      const { template } = item;
      processTemplate(template!, extraColumns);
    }
    return extraColumns;
  };

  const fetchAllUsedFormTemplates = async (): Promise<void> => {
    let extraColumns: Record<string, ColumnDefinition> = {};

    if (projectId) {
      const response = await axiosWrapper.post<GetAllUsedFormTemplatesResponse>(
        `${getBackendUrl()}/projects/getAllUsedFormTemplates`,
        { projectId },
      );
      extraColumns = processTemplatesData(response.data.data ?? []);
      setCustomFields(extraColumns);
    }

    // Explicit type argument: inference would otherwise fall back to the `FilesGridRow` default and
    // reject these templates, which take the narrower `FileData` (parameter contravariance). Erased.
    const allColumns = getFilesGridColumns<FileData>({
      userAvatar,
      formatDate,
      statusInReviewTemplate,
      currentReviewLevelFormat,
      // actionFormatter,
      showProjectID,
      extraColumns,
      getReviewStatus,
      reactGridRef,
    });

    setFilesGridColumns(allColumns);
  };

  const formatDate = (params: FileData): HTMLDivElement => {
    const dateFormat = convertDate(Number(params.createdDate), "dateForTooltip");
    const container = document.createElement("div");
    const root = createRoot(container);

    root.render(
      <ToolTipComponent position="bottom" content={dateFormat}>
        {convertDate(Number(params.createdDate), "dateForText")}
      </ToolTipComponent>,
    );

    return container;
  };

  const userAvatar = (args: FileData): HTMLDivElement => {
    const container = document.createElement("div");
    renderUserTag(container, { id: args.createdBy._id });
    return container;
  };

  const getReviewStatus = (args: FileData): string => {
    const status = _.get(args, "reviews.reviewStatus");
    const currentReviewLevel = args.currentReviewLevel;
    if (currentReviewLevel === "Varies" || currentReviewLevel === "") {
      return currentReviewLevel;
    }
    if (currentReviewLevel) {
      return (currentReviewLevel as { title?: string }).title!;
    }
    if (status === "Closed") {
      return "Review Closed";
    } else if (status === "Revoked") {
      return "Review Revoked";
    } else if (status === "Draft") {
      return "Review Draft";
    } else if (status === "Open") {
      return "Review Open";
    }
    return "";
  };

  const currentReviewLevelFormat = (value: FileData): HTMLDivElement => {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(<span className="italic text-gray-500">{getReviewStatus(value)}</span>);
    return container;
  };

  const statusInReviewTemplate = (args: FileData): string | HTMLDivElement => {
    if (!_.get(args, "reviews.status.name")) {
      return "-";
    }

    const container = document.createElement("div");
    const root = createRoot(container);

    const { name, category } = args.reviews!.status!;
    const style = getStatusBtnStyle(category);

    root.render(
      <p className="w-fit px-2 py-1 rounded-lg text-white font-semibold text-xs" style={style}>
        {name}
      </p>,
    );
    return container;
  };

  const handleFileOpenClick = (params: FileData): void => {
    const { fileVersionId, file } = params;
    const url = getViewerUrl({
      fileVersionId: fileVersionId || "",
      spaceId: _.get(file, "space.id"),
      projectId: (_.get(file, "space.project.id") || projectId) ?? "",
    });

    window.open(url, "_blank");
  };

  const handleReviewOpenClick = (_e: unknown, args: FileData): void => {
    // Erased assertions: this runs only for a row the user selected in a review context.
    const spaceId = args.file!.space!.id;
    const projectId = args.file!.space!.project!.id;
    const reviewId = args.reviews!._id;
    if (reviewId) {
      const url = getReviewUrl(projectId, spaceId, reviewId);
      window.open(url, "_blank");
    }
  };

  const handleOpenResource = (_e: unknown, params: FileData): void => {
    if (params.isContainer) {
      dispatch(
        showToastNotification({
          content: t("Cannot open container"),
          type: TOAST_TYPE_ERROR,
        }),
      );
    } else {
      handleFileOpenClick(params);
    }
  };

  const handleViewProperties = async (): Promise<void> => {
    const args = reactGridRef.current!.dataView.getAllSelectedItems();
    const fileVersionIds = args.map((data: FileData) => data.fileVersionId!);
    setFileVersionId(fileVersionIds);
  };

  useEffect(() => {
    if (showProperties && selectedRowId) {
      handleViewProperties();
    }
  }, [showProperties, selectedRowId]);

  const handlePropertiesOpen = (): void => {
    const next = !showProperties;
    setShowProperties(next);
    if (!next) {
      setFileVersionId([]);
    }
  };

  // `select` hands back `string | number`; this handler only ever receives the numeric ids 1 and 2
  // declared on `items` below. Widened rather than asserted at the prop — no runtime change.
  const handleActionClick = (data: string | number): void => {
    const args = reactGridRef.current!.dataView.getAllSelectedItems()[0];
    if (data === 1) {
      handleOpenResource(undefined, args!);
    } else if (data === 2) {
      handleReviewOpenClick(undefined, args!);
    }
  };

  const handleRowSelection = (args: { detail: { args: { ids: string[] | number[] } } }): void => {
    const selectedData = reactGridRef
      .current!.slickGrid.getSelectedRows()
      .map((data: number) => reactGridRef.current!.slickGrid.getDataItem(data) as FileData);
    const filteredData = selectedData.filter((file: FileData) => !!file && !file.isRowDisabled);
    handleFileSelectionCallback?.(
      filteredData.map((file: FileData) => {
        return {
          id: file._id,
          hierarchyId: file._id,
          name: file.fileName,
          userId: file.createdBy._id,
          dateCreated: file.createdDate,
          versionNumber: file.fileVersionNumber,
          fileVersion: file.fileVersionId,
          size: file.size,
          conversionStatus: file.conversionStatus,
          isContainer: file.isContainer,
          parentId: file.parentId,
          resourceType: file.resourceType,
          project: file.file!.space!.project,
          space: file.space,
          spaceId: file.spaceId,
          fileType: file.fileType,
          engineType: file.engineType,
          isFile: file.isFile,
        };
      }),
    );

    setSelectedRowId(args.detail.args.ids);
  };

  const loadData = async (skip: number, take: number): Promise<void> => {
    // setShowLoader(true);

    const response = (await fetchFilesData(skip, take)) as { result: RawFileRecord[]; reviewsData: RawReviewRecord[] };
    const reviewsByVersionId = mapReviewsByVersionId(response.reviewsData);
    const fullResponse = buildFilesWithReviews(response.result, reviewsByVersionId);
    const modifiedResponse =
      Array.isArray(disableFiles) && disableFiles.length
        ? fullResponse.map((file: FileData) =>
            file.fileVersionId && disableFiles.includes(file.fileVersionId) ? { ...file, isRowDisabled: true } : file,
          )
        : fullResponse;

    setOriginalFilesData(modifiedResponse);

    // Apply version filtering based on showAllVersions ref (use ref to avoid stale closure)
    if (showAllVersionsRef.current) {
      setFilesData(modifiedResponse);
    } else {
      setFilesData(filterToLatestVersions(modifiedResponse));
    }

    setShowLoader(false);

    // Force grid to re-render cells after data loads to ensure formatters/valueGetters are applied
    requestAnimationFrame(() => {
      if (reactGridRef.current?.slickGrid) {
        reactGridRef.current!.slickGrid.invalidate();
      }
    });
  };

  // Fetch files from service
  const fetchFilesData = async (skip: number, take: number) => {
    const queryData = new QueryBuilder()
      .where("parentFileVersionId", "equal", null)
      .requiresCount()
      .skip(skip)
      .take(take);

    if (projectId) {
      queryData.addParams("projectId", projectId);
    }

    if (spaceId) {
      queryData.addParams("spaceId", spaceId);
    }

    if (showOnlyMergedFile) {
      queryData.addParams("showOnlyMergedFile", showOnlyMergedFile);
    }

    return await executeQuery(getFilesList, queryData);
  };

  const mapReviewsByVersionId = (reviewsData: RawReviewRecord[]): Map<string, FileReviewSummary[]> => {
    const map = new Map<string, FileReviewSummary[]>();
    for (const review of reviewsData) {
      for (const { fileVersionId, status } of review.fileVersionStatuses!) {
        if (!fileVersionId) continue;
        const reviews = map.get(fileVersionId!) || [];
        reviews.push({
          _id: review._id,
          createdDate: review.createdDate,
          name: review.name,
          reviewFlow: review.reviewFlow,
          status,
          serialId: review.serialId,
          currentReviewLevel: review.currentReviewLevel,
          nextActionDueDate: review.nextActionDueDate,
          reviewStatus: review.reviewStatus,
          initiationDate: review.initiationDate,
          finishedDate: review.finishedDate,
        });
        map.set(fileVersionId!, reviews);
      }
    }
    return map;
  };

  // Build files array with reviews
  const buildFilesWithReviews = (filesData: RawFileRecord[], reviewsByVersionId: Map<string, FileReviewSummary[]>) => {
    const result: FileData[] = [];

    for (const data of filesData) {
      setFileType(data);

      const reviews = reviewsByVersionId.get(data.id?.toString()!) || [];

      if (reviews.length > 0) {
        for (const [index, review] of reviews.entries()) {
          // Erased, same reason as the no-review push below: `getFilesList` is queried through
          // QueryBuilder/executeQuery, which have no response schema, so `RawFileRecord` cannot
          // promise `createdDate` / `createdBy` / `fileVersionNumber`. The spread is verbatim.
          result.push({
            ...data,
            reviews: review,
            id: `${data.id}_${index}`,
            reviewName: review.name,
            serialId: review.serialId,
            reviewFlow: review.reviewFlow!.name,
            currentReviewLevel: review.currentReviewLevel,
            reviewStatusName: review.status!.name,
            nextActionDueDate: review.nextActionDueDate,
          } as FileData);
        }
      } else {
        // Erased: `RawFileRecord` cannot promise the members `FileData` requires; the spread is
        // exactly what the grid renders today.
        result.push({ ...data, reviews: {} } as FileData);
      }
    }

    return result;
  };

  // Helper: set fileType
  const setFileType = (data: RawFileRecord) => {
    if (data.resourceType === "RESOURCE_TYPE_FILE") {
      const fileName = data.fileName;
      data.fileType = fileName!.includes(".") ? fileName!.substring(fileName!.lastIndexOf(".") + 1).toUpperCase() : "";
    } else {
      data.fileType = "";
    }
  };

  useEffect(() => {
    loadData(0, 1000000000);
  }, []);

  const reactGridRef = useRef<SlickGrid1Ref<FileData> | null>(null);
  const pendingLayoutParamsRef = useRef<FilesGridSavedView | null>(null);

  useEffect(() => {
    if (gridReady && pendingLayoutParamsRef.current) {
      const pendingParams = pendingLayoutParamsRef.current;
      pendingLayoutParamsRef.current = null;
      // Replay after the grid's own view application (it runs in the grid-mounted commit),
      // matching the old ordering where this handler ran after the grid applied the view.
      // suppressViewUpdates keeps the replayed state changes from re-persisting the view.
      setTimeout(() => {
        const grid = reactGridRef.current;
        if (!grid?.slickGrid) return;
        if (grid.suppressViewUpdates) {
          grid.suppressViewUpdates(() => onClickLayout(pendingParams));
        } else {
          onClickLayout(pendingParams);
        }
      }, 0);
    }
  }, [gridReady]);

  useEffect(() => {
    if (gridReady) {
      handleUpdateCurrentView({
        enableGrouping: groupActive,
      });
    }
  }, [gridReady, groupActive]);

  useEffect(() => {
    handleUpdateCurrentView({
      enableAllVersions: showAllVersions,
    });
  }, [showAllVersions]);

  const handleFileUpload = (): void => {
    if (importOnlyUploads) {
      return;
    }
    if (fileUploadState === "MINIMISED") {
      dispatch(
        showToastNotification({
          content: t("Another upload is in progress. Please close that popup to proceed."),
          type: TOAST_TYPE_ERROR,
        }),
      );
      return;
    }

    dispatch(
      setCurrentFileUploadProperties({
        fileAllowedExtensions: {
          minFileSize: 0,
          maxFileSize: MAX_FILE_UPLOAD_SIZE,
          allowedExtensions: "",
        },
        isUploadAllowed: true,
        // getPermission,
        formTemplate: formTemplateRef.current,
      }),
    );
    dispatch(setCurrentFolderInFileManager({ hierarchyId: "0" }));
    dispatch(maximiseFileUpload());
  };

  const handleGroupActive = (): void => {
    setGroupActive(!groupActive);
  };

  const handleFilterActive = (): void => {
    setFilterActive(!filterActive);
  };

  const handleShowAllVersions = useCallback((): void => {
    setShowAllVersions((prev) => {
      if (prev) {
        setFilesData(filterToLatestVersions(originalFilesData));
      } else {
        setFilesData(originalFilesData);
      }
      return !prev;
    });
  }, [originalFilesData]);

  const updateItemData = (
    item: FileData,
    fileVersionIdToMatch: string[] | null,
    dataKey: string,
    dataValue: unknown,
  ): boolean => {
    if (!fileVersionIdToMatch || !Array.isArray(fileVersionIdToMatch)) return false;
    if (!fileVersionIdToMatch.includes(String(item.id))) return false;
    _.update(item, dataKey, () => dataValue);
    return true;
  };

  const processDataKey = (
    dataKey: string,
    data: Record<string, unknown>,
    items: FileData[],
    fileVersionId: string[] | null,
  ): boolean => {
    return items.some((item: FileData) => updateItemData(item, fileVersionId, dataKey, data[dataKey]));
  };

  const onChange = (data: Record<string, unknown>): void => {
    const { dataView, slickGrid } = reactGridRef.current!;
    // `items` is `protected` on SlickDataView; single erased assertion to the view this file needs.
    const items = (dataView as typeof dataView & { items: FileData[] }).items;

    for (const dataKey of Object.keys(data)) {
      processDataKey(dataKey, data, items, fileVersionId);
    }

    slickGrid.invalidate();
  };

  const actionTemplate = (): React.ReactElement => {
    return (
      <TypedDropdownButtonComponent
        disabled={selectedRowId?.length !== 1}
        // buttonType={BUTTON_TYPES.DROPDOWN_BACKGROUND_BUTTON}
        content={t("Action")}
        select={handleActionClick}
        items={[
          {
            text: t("Open File"),
            id: 1,
          },
          {
            text: t("Open Review"),
            id: 2,
          },
        ]}
      />
    );
  };

  const toolbarClickHandler = (args: string): void => {
    if (args === "upload") {
      handleFileUpload();
    } else if (args === "group") {
      handleGroupActive();
    } else if (args === "filter") {
      handleFilterActive();
    }
    if (args === "properties") {
      handlePropertiesOpen();
    }
  };

  const toolbar = [
    showToolbar.upload &&
      !importOnlyUploads && {
        name: t("Upload"),
        id: "upload",
        buttonType: BUTTON_TYPES.BACKGROUND_BUTTON,
      },
    showToolbar.action && {
      id: "action",
      template: actionTemplate,
    },
  ].filter(Boolean) as ToolbarItem[];

  const [enableWrapText, setEnableWrapText] = useState<boolean>(false);

  return (
    <div className="flex gap-4 h-full w-full">
      <SplitLayout
        initialSizes={showProperties ? [66, 34] : [100, 0]}
        gutterWidth={"0.25rem"}
        minSizes={[50, 20]}
        style={{ display: "flex", height: "100%" }}
        showProperties={showProperties}
      >
        <GridPageLayout
          showHeader={showTitle}
          customHeader={
            <FilesGridHeader
              projectId={projectId}
              showAllVersions={showAllVersions}
              onToggleVersions={handleShowAllVersions}
            />
          }
          className="min-w-0"
          gridContainerClassName="items-center justify-center overflow-auto"
        >
          {filesGridColumns.length > 0 && (
            <>
              {showLoader ? (
                <CircularProgress />
              ) : (
                <SlickGrid1
                  // `ValidatedColumnDefinition` carries a brand only `typedColumns()` can mint.
                  // These columns are assembled by `getFilesGridColumns`, so the brand is asserted
                  // here via an intersection that includes the real type — one erased `as`.
                  columnDefinitions={
                    filesGridColumns as typeof filesGridColumns & ValidatedColumnDefinition<FileData>[]
                  }
                  enableWrapText={enableWrapText}
                  setEnableWrapText={setEnableWrapText}
                  gridOptions={{
                    showPreHeaderPanel: Object.keys(customFields).length > 0,
                    enableAutoSizeColumns: false,
                  }}
                  // Both handlers take shapes narrower than the props declare: `onClickLayout`
                  // reads this grid's two extra saved-view keys, and `onSelectedRowIdsChanged`
                  // reads `detail.args.ids`. Erased assertions; the callbacks are unchanged.
                  onClickLayout={onClickLayout as (layout: LayoutData) => void}
                  onSelectedRowIdsChanged={handleRowSelection as typeof handleRowSelection & ((args: unknown) => void)}
                  setGridReady={setGridReady}
                  triggerUpdateCurrentView={triggerUpdateCurrentView}
                  dataset={filesData}
                  enableGroupingOption
                  enableFilteringOption
                  enableSearchBar
                  {...(projectId &&
                    showToolbar.views && {
                      viewsOptions: {
                        headerName: "Views",
                        name: "View",
                        resourceType: "FILES",
                        selectedLayoutKey: "FILES_SELECTED_LAYOUT",
                        fetchParams: {
                          projectId,
                        },
                      },
                    })}
                  enableViews={!!(projectId && showToolbar.views)}
                  disableMenuOptions={disableMenuOptions}
                  enableSortingOption
                  enableColumnChooser
                  enableRowResize={true}
                  toolbar={toolbar}
                  handleUpdateCurrentView={handleUpdateCurrentView}
                  ref={reactGridRef}
                  excelExport={excelExport}
                  toolbarClickHandler={toolbarClickHandler}
                  propertiesPanel={propertiesPanel}
                />
              )}
            </>
          )}
        </GridPageLayout>
        {showProperties && (
          <div className="w-full h-full">
            <FileVersion
              fileVersionId={fileVersionId}
              isContainer={reactGridRef.current!.dataView.getAllSelectedItems()[0]?.isContainer}
              onChange={onChange}
              disableEngineType={false}
            />
          </div>
        )}
      </SplitLayout>
    </div>
  );
}

export default FilesGrid;
